import React from 'react'
import { MFAMethod } from '@mobi/api-types/src/mfa'
import { useNavigate, useParams } from 'react-router'
import { isMfaError, mfaCodeText, VerificationStandardError } from '../Helpers/helpers'
import { mapMFAMethodToAttributeName } from '../Helpers/utils'
import {
  startCognitoAttributeVerification,
  useMfaCompleteVerification,
} from '../Hooks/useMfaVerification'
import { VerificationCodeForm } from '../Components/VerificationCodeForm'
import { MainContainer } from '@core/Components/Containers'
import { SettingsHeader } from '@core/Areas/Settings/SettingsHeader'
import {
  MfaCodePageHeader,
  MfaCodePageText,
  MfaVerificationCodePageContainer,
} from './VerificationCodePage.styles'
import { useLogon } from '@core/Utils/hooks'
import { useCognitoUser } from '@core/Data/Account/useCognitoUser'
import { addToast } from '@mobi/component-library/Feedback/Toast'
import { useMfaUpdateAuthMutation } from '../Hooks/useMfaUpdateAuthMutation'

interface VerificationCodeContentProps {
  preferredFactor: MFAMethod
  accountNumber: number
}

export const VerificationCodePage: React.FC = () => {
  const { preferredFactor } = useParams()
  const { accountNumber } = useLogon()
  const validatedPreferredFactor = validatePreferredFactor(preferredFactor ?? 'SMS') ?? 'SMS'

  return accountNumber ? (
    <VerificationCodeContent
      preferredFactor={validatedPreferredFactor}
      accountNumber={accountNumber}
    />
  ) : null
}

const VerificationCodeContent: React.FC<VerificationCodeContentProps> = ({
  preferredFactor,
  accountNumber,
}) => {
  const navigate = useNavigate()
  const { data: cognitoUser } = useCognitoUser(accountNumber)
  const { completeVerification } = useMfaCompleteVerification()
  const { mutateAsync: updateMfaPreference } = useMfaUpdateAuthMutation()

  const handleVerify = async (code: string): Promise<void> => {
    try {
      await completeVerification({
        attributeName: mapMFAMethodToAttributeName(preferredFactor),
        code: code,
      })

      if (cognitoUser?.preferredFactor === 'None') {
        // if the user doesn't have MFA on already, we want to
        // enable it for the verified factor to save them a click
        await updateMfaPreference({ preferredFactor })

        addToast({
          id: 'mfa-code-success',
          isDismissable: true,
          title: 'Contact verified & enabled for MFA',
          icon: 'success',
        })
      } else {
        addToast({
          id: 'mfa-code-success',
          isDismissable: true,
          title: 'Contact verified successfully',
          icon: 'success',
        })
      }

      navigate('/account/settings/mfa/', {
        replace: true,
      })
    } catch (error) {
      if (isMfaError(error)) {
        if (error.status === 403) {
          throw new VerificationStandardError('Sorry the code you have provided is incorrect')
        } else if (error.status === 429) {
          throw new VerificationStandardError('Attempt limit exceeded, please try after some time.')
        }
      } else {
        throw new VerificationStandardError('Verification failed')
      }
    }
  }

  const handleResend = async (): Promise<void> => {
    try {
      await startCognitoAttributeVerification({
        attributeName: mapMFAMethodToAttributeName(preferredFactor),
      })
      return
    } catch (error) {
      if (isMfaError(error)) {
        if (error.status === 429) {
          throw new VerificationStandardError('Attempt limit exceeded, please try after some time.')
        }
      } else {
        throw new VerificationStandardError('Code resend has failed')
      }
    }
  }

  const handleEditContact = () => {
    navigate('/account/contactdetails', { replace: true })
  }

  if (cognitoUser === undefined) {
    return (
      <MainContainer data-tid-mfa-verification-container>
        <MfaCodePageHeader>Verify your contact details</MfaCodePageHeader>
        <p>There was an error trying to verify your contact details.</p>
      </MainContainer>
    )
  }

  const contactDetail =
    preferredFactor === 'SMS'
      ? (cognitoUser.userAttributes.phone_number ?? '')
      : (cognitoUser.userAttributes.email ?? '')

  return (
    <MainContainer data-tid-mfa-verification-page>
      <SettingsHeader title='Settings' />
      <MfaVerificationCodePageContainer>
        <VerificationCodeForm
          onVerify={handleVerify}
          onResend={handleResend}
          onEditContact={handleEditContact}
          analyticsFlowName='Settings'
          analyticsMethodName={preferredFactor}
        >
          <MfaCodePageText>{mfaCodeText(preferredFactor, contactDetail)}</MfaCodePageText>
        </VerificationCodeForm>
      </MfaVerificationCodePageContainer>
    </MainContainer>
  )
}

function validatePreferredFactor(factorString: string): MFAMethod | undefined {
  switch (factorString) {
    case 'None':
    case 'SMS':
    case 'Email':
    case 'SoftwareToken':
    case 'Unknown':
      return factorString as MFAMethod
    default:
      return undefined
  }
}
